import { DocumentSelectionGroup, DocumentSubtype, PrestigeProductKinds, ProductKind, ProductKindsFloorPlan, ProductKindsRequiringFloorPlanCertification, ProductSegment, ProductType, ProductTypesFloorPlan, ProductTypesRequiringFloorPlanCertification } from 'constants/product'
import { CatalogueBundledProduct, CatalogueOptionProduct, CatalogueProduct } from 'dataQueries/purchase.query'
import { Reducer, useEffect, useMemo, useReducer, useState } from 'react'
import { AnalyticsEvent, logAnalyticsEvent, standardDebouncedLogAnalyticsEvent } from 'utils/analytics'

import constate from 'constate'
import { resolveProductGroupKind } from 'constants/purchaseFlow'
import { FeeDTO } from 'models/fee'
import { logger } from 'utils/logging'
import { Nullable } from 'models/helpers'
import { SkyReplacementOption } from 'models/purchaseFlow'
import { usePurchaseFlowConfig } from './PurchaseFlowConfig.context'
import { useEmbedConfig } from './EmbedConfig.context'

interface ProductInventory {
  selectedSegment: ProductSegment | null
  selectedSegmentIndex: number | null
  selectedFilterKinds: Set<ProductKind>
  selectedFilterTypes: Set<ProductType>
  selectedProducts: Record<number, PurchaseFlowProduct>
  selectedProductOptions: Record<number, Record<number, PurchaseFlowProductOption>>
}

/** Possible actions for RoomInventory reducer */
enum ProductInventoryActionType {
  UPDATE_SEGMENT = 'UPDATE_SEGMENT',
  UPDATE_SELECTED_PRODUCT = 'UPDATE_SELECTED_PRODUCT',
  TOGGLE_FILTER_KIND = 'TOGGLE_FILTER_KIND',
  TOGGLE_FILTER_TYPE = 'TOGGLE_FILTER_TYPE',
  SELECT_PRODUCT = 'SELECT_PRODUCT',
  UNSELECT_PRODUCT = 'UNSELECT_PRODUCT',
  SELECT_PRODUCT_OPTION = 'SELECT_PRODUCT_OPTION',
  UNSELECT_PRODUCT_OPTION = 'UNSELECT_PRODUCT_OPTION',
  BULK_UPDATE_PRICES = 'BULK_UPDATE_PRICES',
  RESET = 'RESET',
}

/** Describes room inventory action */
interface ProductInventoryAction {
  type: ProductInventoryActionType
}

/** Action for updating selected segment */
class UpdateSegmentAction implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.UPDATE_SEGMENT
  constructor(
    public segment: ProductSegment | null,
    public segmentIndex: number | null,
    public availableKindsForNewSegment: Set<ProductKind>,
    public availableTypesForNewSegment: Set<ProductType>
  ) { }
}

class UpdateSelectedProductAction implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.UPDATE_SELECTED_PRODUCT
  constructor(
    public productId: number,
    public newProductData: Partial<PurchaseFlowProduct>
  ) { }
}

export class ToggleFilterKind implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.TOGGLE_FILTER_KIND
  constructor(
    public productKind: ProductKind
  ) { }
}

class ToggleFilterType implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.TOGGLE_FILTER_TYPE
  constructor(
    public productType: ProductType
  ) { }
}

class SelectProductAction implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.SELECT_PRODUCT
  constructor(
    public product: CatalogueProduct,
    public quantity: number
  ) { }
}

class UnselectProductAction implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.UNSELECT_PRODUCT
  constructor(
    public productId: number
  ) { }
}

class SelectProductOptionAction implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.SELECT_PRODUCT_OPTION
  constructor(
    public parentId: number,
    public option: CatalogueOptionProduct,
    public quantity: number
  ) { }
}

export class UnselectProductOptionAction implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.UNSELECT_PRODUCT_OPTION
  constructor(
    public parentId: number,
    public optionId: number
  ) { }
}

class BulkUpdatePricesAction implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.BULK_UPDATE_PRICES
  constructor(
    public priceMap: Map<number, FeeDTO>
  ) { }
}

class ResetAction implements ProductInventoryAction {
  readonly type = ProductInventoryActionType.RESET
}

/** Union type of possible actions */
type ProductInventoryActions = UpdateSegmentAction
  | SelectProductAction
  | UnselectProductAction
  | SelectProductOptionAction
  | UnselectProductOptionAction
  | ToggleFilterKind
  | ToggleFilterType
  | UpdateSelectedProductAction
  | BulkUpdatePricesAction
  | ResetAction

export interface PurchaseFlowProduct {
  id: number
  quantity: number
  type: ProductType
  selected?: boolean
  value?: number
  kind: ProductKind
  version?: string
  feePrice: FeeDTO
  /** Base (public) pricebook price, present on channel-priced lines so the FE can strike it through. */
  originalPrice?: Nullable<FeeDTO>
  /** @deprecated */
  shootingDuration?: number
  timeOnSiteMin: Nullable<number>
  timeOnSiteMax: Nullable<number>
  comment?: string
  isBestSeller: boolean
  isLandRegistrationNeeded: boolean
  /** Subtype of an authorities-document product, or null — backend-computed, read instead of `type` */
  documentSubtype?: Nullable<DocumentSubtype>
  /** Mutually-exclusive selection group, or null — backend-computed, see CatalogueProduct (ISSUE-2866) */
  documentSelectionGroup?: Nullable<DocumentSelectionGroup>
  bundledProducts: CatalogueBundledProduct[]
}

/**
 * Flattens the selection record into the sorted `{ productId, quantity }` payload the pricing endpoint
 * expects for live OneSystem repricing. Drops non-positive quantities and sorts by product id so the
 * payload (and the derived selection signature) is stable regardless of insertion order.
 */
export const selectionToPayload = (
  selected: Record<number, PurchaseFlowProduct>
): Array<{ productId: number, quantity: number }> =>
  Object.values(selected)
    .filter((product) => product.quantity > 0)
    .sort((a, b) => a.id - b.id)
    .map((product) => ({ productId: product.id, quantity: product.quantity }))

/**
 * Ids of already-selected products the given product displaces: every product of the same
 * backend-computed `documentSelectionGroup` resolves to the same single DocEstate document, so
 * selecting one must unselect the others (ISSUE-2866). Empty when the product is in no group.
 */
export const productIdsDisplacedBySelectionGroup = (
  product: Pick<PurchaseFlowProduct, 'id' | 'documentSelectionGroup'>,
  selected: Record<number, PurchaseFlowProduct>
): number[] =>
  product.documentSelectionGroup
    ? Object.values(selected)
      .filter((candidate) => candidate.documentSelectionGroup === product.documentSelectionGroup && candidate.id !== product.id)
      .map((candidate) => candidate.id)
    : []

/**
 * An On-Site Area Report ordered on its own sends a technician to an appointment, not a photographer to a
 * shoot — the instruction copy branches on this, and the flow hides the shooting-only sections (ISSUE-2861).
 * Decided over the ordered products and what they bundle, so a bundle pairing measurement with a shooting
 * kind is not mistaken for measurement-only. Option products are deliberately excluded: an option
 * attached to the measurement product would otherwise make a measurement-only order look mixed.
 * Bundled kinds sit behind the same `!!kind` guard as the selectedKinds loop, so a kind-less bundled
 * product is skipped rather than counted as a non-measurement kind.
 */
export const isMeasurementOnlySelection = (selected: Record<number, PurchaseFlowProduct>): boolean => {
  const orderedKinds = Object.values(selected)
    .flatMap((product) => [
      product.kind,
      ...product.bundledProducts.filter((bundled) => !!bundled.kind).map((bundled) => bundled.kind)
    ])

  return orderedKinds.length > 0 && orderedKinds.every((kind) => kind === ProductKind.MEASUREMENT_ON_SITE)
}

/**
 * Whether the selection needs the land-registration (property-information) step. A package product is
 * never land-registration bound itself — the flag lives on its members — so reading the ordered lines
 * alone skips the step for every authorities-document package and sends DocEstate an order with no folios.
 */
export const isLandRegistrationSelection = (selected: Record<number, PurchaseFlowProduct>): boolean =>
  Object.values(selected).some((product) =>
    product.isLandRegistrationNeeded ||
    (product.bundledProducts ?? []).some((bundled) => bundled.isLandRegistrationNeeded)
  )

/**
 * Whether the selection contains a land-registration document — the subtype whose products require the
 * landlord block and a named land-register sheet. Read over the ordered lines AND the products they bundle:
 * a package carries no subtype of its own, and it is the members that reach the authorities. Replaces a
 * hardcoded product-id list, which matched the same document by id when ordered alone and missed it when
 * ordered inside a package.
 */
export const isLandRegistrationDocumentSelection = (selected: Record<number, PurchaseFlowProduct>): boolean =>
  Object.values(selected).some((product) =>
    product.documentSubtype === DocumentSubtype.LAND_REGISTRATION ||
    (product.bundledProducts ?? []).some((bundled) => bundled.documentSubtype === DocumentSubtype.LAND_REGISTRATION)
  )

export interface PurchaseFlowProductOption {
  id: number
  quantity: number
  selected?: boolean
  value?: number
  kind: ProductKind
  version?: string
  feePrice: FeeDTO
  /** @deprecated */
  shootingDuration?: number
  timeOnSiteMin: Nullable<number>
  timeOnSiteMax: Nullable<number>
  comment?: string
}

const initialInventory: ProductInventory = {
  selectedSegment: null,
  selectedSegmentIndex: null,
  selectedFilterKinds: new Set([]),
  selectedFilterTypes: new Set([]),
  selectedProducts: {},
  selectedProductOptions: {},
}

/** Reducer function that handles updating roomInventory state according to dispatched actions */
export const reduceProductInventory: Reducer<ProductInventory, ProductInventoryActions> = (state = initialInventory, action) => {
  switch (action.type) {
    case ProductInventoryActionType.UPDATE_SEGMENT: {
      const newSelectedKinds = new Set(state.selectedFilterKinds)
      const newSelectedTypes = new Set(state.selectedFilterTypes)

      state.selectedFilterKinds.forEach((kind) => {
        if (action.availableKindsForNewSegment.has(kind)) return
        newSelectedKinds.delete(kind)
      })

      state.selectedFilterTypes.forEach((type) => {
        if (newSelectedTypes.has(type)) return
        newSelectedTypes.delete(type)
      })

      return {
        ...state,
        selectedSegment: action.segment,
        selectedSegmentIndex: action.segmentIndex,
        selectedProducts: {},
        selectedProductOptions: {},
        selectedFilterKinds: newSelectedKinds,
        selectedFilterTypes: newSelectedTypes,
      }
    }

    case ProductInventoryActionType.TOGGLE_FILTER_KIND: {

      const newKinds = new Set(state.selectedFilterKinds)

      const isDelete = newKinds.has(action.productKind)

      if (isDelete) newKinds.delete(action.productKind)
      else newKinds.add(action.productKind)

      const newProducts = { ...state.selectedProducts }
      const newProductsOptions = { ...state.selectedProductOptions }

      if (isDelete) {
        for (const product of Object.values(newProducts)) {
          // Filter kinds are the (possibly overridden) group kinds, so de-selection must
          // resolve the product's group kind too — e.g. AI_PHOTO_EDITING grouped under PHOTO.
          if (resolveProductGroupKind(product.kind) !== action.productKind) continue
          delete newProducts[product.id]
          delete newProductsOptions[product.id]
        }
      }

      return {
        ...state,
        selectedFilterKinds: newKinds,
        selectedProducts: newProducts,
        selectedProductOptions: newProductsOptions,
      }
    }

    case ProductInventoryActionType.TOGGLE_FILTER_TYPE: {

      const newTypes = new Set(state.selectedFilterTypes)

      const isDelete = newTypes.has(action.productType)

      if (isDelete) newTypes.delete(action.productType)
      else newTypes.add(action.productType)

      const newProducts = { ...state.selectedProducts }
      const newProductsOptions = { ...state.selectedProductOptions }

      if (isDelete) {
        for (const product of Object.values(newProducts)) {
          if (product.type !== action.productType) continue
          delete newProducts[product.id]
          delete newProductsOptions[product.id]
        }
      }

      return {
        ...state,
        selectedFilterTypes: newTypes,
        selectedProducts: newProducts,
        selectedProductOptions: newProductsOptions,
      }
    }

    case ProductInventoryActionType.SELECT_PRODUCT:
      return {
        ...state,
        selectedProducts: {
          ...state.selectedProducts,
          [action.product.id]: {
            ...action.product,
            quantity: action.quantity,
            comment: undefined,
          }
        }
      }

    case ProductInventoryActionType.UNSELECT_PRODUCT: {
      const { [action.productId]: _, ...prunedProductOptions } = state.selectedProductOptions
      const { [action.productId]: __, ...prunedProducts } = state.selectedProducts

      return {
        ...state,
        selectedProducts: prunedProducts,
        selectedProductOptions: prunedProductOptions,
      }
    }

    case ProductInventoryActionType.SELECT_PRODUCT_OPTION:
      return {
        ...state,
        selectedProductOptions: {
          ...state.selectedProductOptions,
          [action.parentId]: {
            ...(state.selectedProductOptions[action.parentId] || {}),
            [action.option.id]: {
              ...action.option,
              quantity: action.quantity,
            },
          },
        },
      }

    case ProductInventoryActionType.UNSELECT_PRODUCT_OPTION: {
      // No-op when the option isn't actually selected — returning a new state
      // reference here would invalidate downstream memos (e.g. allSelectedFloorPlanProducts)
      // and can self-perpetuate effects that dispatch UNSELECT_PRODUCT_OPTION (ISSUE-1733).
      if (!state.selectedProductOptions[action.parentId]?.[action.optionId]) return state

      const newProductOptions = { ...state.selectedProductOptions[action.parentId] }
      delete newProductOptions[action.optionId]

      return {
        ...state,
        selectedProductOptions: {
          ...state.selectedProductOptions,
          [action.parentId]: newProductOptions,
        },
      }
    }

    case ProductInventoryActionType.UPDATE_SELECTED_PRODUCT: {
      if (!state.selectedProducts[action.productId]) return { ...state }

      return {
        ...state,
        selectedProducts: {
          ...state.selectedProducts,
          [action.productId]: {
            ...state.selectedProducts[action.productId],
            ...action.newProductData,
          }
        }
      }
    }

    case ProductInventoryActionType.BULK_UPDATE_PRICES: {
      const newProducts = { ...state.selectedProducts }
      const newProductOptions = { ...state.selectedProductOptions }

      for (const productId in newProducts) {
        const newPrice = action.priceMap.get(newProducts[productId].id)
        if (newPrice) {
          newProducts[productId] = { ...newProducts[productId], feePrice: newPrice }
        }
      }

      for (const parentId in newProductOptions) {
        const options = { ...newProductOptions[parentId] }
        for (const optionId in options) {
          const newPrice = action.priceMap.get(options[optionId].id)
          if (newPrice) {
            options[optionId] = { ...options[optionId], feePrice: newPrice }
          }
        }
        newProductOptions[parentId] = options
      }

      return {
        ...state,
        selectedProducts: newProducts,
        selectedProductOptions: newProductOptions,
      }
    }

    case ProductInventoryActionType.RESET:
      return initialInventory

    default:
      return { ...state }
  }
}

/**
 * Where a committed area segment came from, for Size_property_selected (ISSUE-4022): auto-selected from the
 * embed property's surface, committed on the embed's manual area gate, or the only band of a one-band catalogue
 * that the embed committed by itself. A plain slider pick passes none.
 */
export type SegmentOrigin = 'property_surface' | 'size_gate' | 'single_band'

export const [PurchaseFlowProductsProvider, usePurchaseFlowProducts] = constate(() => {
  const {
    availableSegments,
    getProductCatalogue,
    availableProductKindsPerSegment,
    availableProductTypesPerSegment,
  } = usePurchaseFlowConfig()
  const { isEmbed } = useEmbedConfig()

  // AI settings - in case the settings get complex, consider moving them to a separate context
  const [isSkyReplacementEnabled, setIsSkyReplacementEnabled] = useState(false)
  const [selectedSkyReplacement, setSelectedSkyReplacement] = useState<SkyReplacementOption | null>(null)

  /** Holds all selected products and filters */
  const [productInventory, dispatch] = useReducer(reduceProductInventory, initialInventory)

  function setSegment(segment: ProductSegment, segmentIndex: number, origin?: SegmentOrigin): void
  function setSegment(segment: null, segmentIndex: null): void
  function setSegment(segment: ProductSegment | null, segmentIndex: number | null, origin?: SegmentOrigin): void {
    // size_prefilled / gate_shown tell a forced embed pick from a routine one (ISSUE-4022)
    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_SIZE_PROPERTY_SELECTED, {
      Segment: segment,
      size_prefilled: origin === 'property_surface',
      gate_shown: origin === 'size_gate',
    })

    dispatch(new UpdateSegmentAction(
      segment,
      segmentIndex,
      !!segment && !!availableProductKindsPerSegment[segment]?.length
        ? new Set(availableProductKindsPerSegment[segment]?.map(({ productKind }) => productKind))
        : new Set([]),
      !!segment && !!availableProductTypesPerSegment[segment]?.all.length
        ? new Set(availableProductTypesPerSegment[segment]?.all.map(({ productType }) => productType))
        : new Set([])
    ))
  }

  const selectOptionProduct = (parentId: number, option: CatalogueOptionProduct, quantity: number) => {
    const parentProduct = productInventory.selectedProducts[parentId]
    if (parentProduct) {
      logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_OPTION_SELECTED, {
        ProductKind: parentProduct.kind,
        ProductType: parentProduct.type
      })
    }

    dispatch(new SelectProductOptionAction(parentId, option, quantity))
  }

  const unselectOptionProduct = (parentId: number, optionId: number) => {
    const parentProduct = productInventory.selectedProducts[parentId]
    if (parentProduct) {
      logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_OPTION_REMOVED, {
        ProductKind: parentProduct.kind,
        ProductType: parentProduct.type
      })
    }

    dispatch(new UnselectProductOptionAction(parentId, optionId))
  }

  const selectProduct = (product: CatalogueProduct, quantity: number) => {
    dispatch(new SelectProductAction(product, quantity))
  }

  const unselectProduct = (productId: number) => {
    dispatch(new UnselectProductAction(productId))
  }

  const setProductComment = (productId: number, comment: string | undefined) => {
    dispatch(new UpdateSelectedProductAction(productId, { comment }))
  }

  const updateSelectedProduct = (productId: number, newProductData: Partial<PurchaseFlowProduct>) => {
    dispatch(new UpdateSelectedProductAction(productId, newProductData))
  }

  const toggleFilterKind = (productKind: ProductKind) => {
    const willBeSelected = !productInventory.selectedFilterKinds.has(productKind)

    dispatch(new ToggleFilterKind(productKind))
    standardDebouncedLogAnalyticsEvent('product_type_selected', { productType: productKind, selected: willBeSelected })
  }

  const toggleFilterType = (productType: ProductType) => {
    const willBeSelected = !productInventory.selectedFilterTypes.has(productType)

    dispatch(new ToggleFilterType(productType))
    standardDebouncedLogAnalyticsEvent('product_type_selected', { productType, selected: willBeSelected })
  }

  const updateProductPrices = (priceMap: Map<number, FeeDTO>) => {
    const selectedIds = Object.values(productInventory.selectedProducts).map((p) => p.id)
    const unmatchedIds = selectedIds.filter((id) => !priceMap.has(id))
    if (unmatchedIds.length > 0) {
      logger.info('Price update mismatch: some selected products not in recalculated prices', undefined, {
        context: { component: 'PurchaseFlowProducts', metadata: { unmatchedIds, selectedIds, priceMapEntries: Array.from(priceMap.entries()) } },
      })
    }
    dispatch(new BulkUpdatePricesAction(priceMap))
  }

  const resetProducts = () => {
    dispatch(new ResetAction())
  }

  const computedProductProperties = useMemo(() => {
    const selectedKindsArray: Array<ProductKind> = []
    const selectedProductTypes: Set<ProductType> = new Set([])
    let isPrestigeProductSelected = false

    for (let product of Object.values(productInventory.selectedProducts)) {

      const optionsArray = Object.values(productInventory.selectedProductOptions[product.id] || {})

      // Add product's kind to array
      if (!!product.kind) {
        selectedKindsArray.push(product.kind)
      }

      // Add product's type to set
      if (!!product.type) {
        selectedProductTypes.add(product.type)
      }

      // Keep track if any prestigeProduct has been selected
      if (!isPrestigeProductSelected && PrestigeProductKinds.has(product.kind)) isPrestigeProductSelected = true

      // Add all selected options' kinds to array
      selectedKindsArray.push(...optionsArray.map((option) => option.kind))

      // Add bundled products kinds and types
      product.bundledProducts.forEach((bundledProduct) => {
        if (!!bundledProduct.type) {
          selectedProductTypes.add(bundledProduct.type)
        }

        if (!!bundledProduct.kind) {
          selectedKindsArray.push(bundledProduct.kind)
        }
      })
    }

    return {
      selectedKinds: new Set(selectedKindsArray),
      selectedProductTypes,
      isPrestigeProductSelected,
    }
  }, [productInventory.selectedProductOptions, productInventory.selectedProducts])

  // Stable key that only changes when product selection (IDs/quantities) changes,
  // NOT when prices change (e.g. from recalculate-prices VAT update via BULK_UPDATE_PRICES).
  const selectedProductsKey = useMemo(
    () => Object.values(productInventory.selectedProducts).map((p) => `${p.id}:${p.quantity}`).sort().join(','),
    [productInventory.selectedProducts]
  )

  const selectedProductOptionsKey = useMemo(
    () => Object.entries(productInventory.selectedProductOptions)
      .flatMap(([parentId, options]) =>
        Object.values(options).map((o) => `${parentId}:${o.id}:${o.quantity}`)
      )
      .sort()
      .join(','),
    [productInventory.selectedProductOptions]
  )

  // Measurement product check
  const hasMeasurementProduct = useMemo(() => Array.from(computedProductProperties.selectedKinds).some((kind) => kind === ProductKind.MEASUREMENT_ON_SITE), [computedProductProperties.selectedKinds])

  // Measurement-only order check — the why lives on isMeasurementOnlySelection (ISSUE-2861)
  const isOnlyMeasurementProduct = useMemo(
    () => isMeasurementOnlySelection(productInventory.selectedProducts),
    [productInventory.selectedProducts]
  )

  // Document product check
  const hasDocumentProduct = useMemo(() => Array.from(computedProductProperties.selectedKinds).some((kind) => kind === ProductKind.AUTHORITIES_DOCUMENTS), [computedProductProperties.selectedKinds])

  // Energy certificate products checks
  const hasConsumptionCertificateProduct = useMemo(() => Array.from(computedProductProperties.selectedProductTypes).some((type) => type === ProductType.CONSUMPTION_CERTIFICATE), [computedProductProperties.selectedProductTypes])
  const hasEnergyCertificateProduct = useMemo(() => Array.from(computedProductProperties.selectedProductTypes).some((type) => type === ProductType.ENERGY_CERTIFICATE_OF_NEED), [computedProductProperties.selectedProductTypes])

  // Floor plan products checks
  const hasFloorPlanCertificationType = useMemo(() => Array.from(computedProductProperties.selectedProductTypes).some((type) => ProductTypesRequiringFloorPlanCertification.has(type)), [computedProductProperties.selectedProductTypes])
  const hasFloorPlanConfigType = useMemo(() => Array.from(computedProductProperties.selectedProductTypes).some((type) => ProductTypesFloorPlan.has(type)), [computedProductProperties.selectedProductTypes])
  const hasFloorPlanConfigKind = useMemo(() => Array.from(computedProductProperties.selectedKinds).some((kind) => ProductKindsFloorPlan.has(kind)), [computedProductProperties.selectedKinds])
  const hasFloorPlanCertificationKind = useMemo(() => Array.from(computedProductProperties.selectedKinds).some((kind) => ProductKindsRequiringFloorPlanCertification.has(kind)), [computedProductProperties.selectedKinds])
  const hasFloorPlanProduct = useMemo(() => hasFloorPlanConfigKind || (hasFloorPlanConfigType && !hasMeasurementProduct), [hasFloorPlanConfigKind, hasFloorPlanConfigType, hasMeasurementProduct])
  const hasFloorPlanCertificationProduct = useMemo(() => hasFloorPlanCertificationType && hasFloorPlanCertificationKind, [hasFloorPlanCertificationKind, hasFloorPlanCertificationType])

  const floorPlanProduct = useMemo(() => {
    if (!hasFloorPlanProduct) return undefined

    // First, let's search between main products
    const selectedProduct = Object.values(productInventory.selectedProducts).find(product => (ProductTypesFloorPlan.has(product.type) || ProductKindsFloorPlan.has(product.kind)))

    if (selectedProduct) return selectedProduct

    // Second, search between options
    let selectedOption = undefined
    Object.values(productInventory.selectedProductOptions).find(productOptions => {

      selectedOption = Object.values(productOptions).find(option => ProductKindsFloorPlan.has(option.kind))
      return !!selectedOption
    })

    return selectedOption
  }, [hasFloorPlanProduct, productInventory.selectedProductOptions, productInventory.selectedProducts])

  // Energy certificate product check
  const requiresEnergyCertificateConfig = useMemo(() => Array.from(computedProductProperties.selectedProductTypes).some((type) => type === ProductType.ENERGY_CERTIFICATE_OF_NEED), [computedProductProperties.selectedProductTypes])

  // Consumption certificate product check
  const requiresConsumptionCertificateConfig = useMemo(() => Array.from(computedProductProperties.selectedProductTypes).some((type) => type === ProductType.CONSUMPTION_CERTIFICATE), [computedProductProperties.selectedProductTypes])

  // AI settings product check
  const requiresAIEditingConfig = useMemo(() => (
    isSkyReplacementEnabled &&
    Array.from(computedProductProperties.selectedProductTypes).some((kind) => kind === ProductType.AI_PHOTO_EDITING)
  ), [computedProductProperties.selectedProductTypes, isSkyReplacementEnabled])

  // If only one segment is available, select it automatically. Not in the embed: there ProductStep owns the pick
  // (from the property surface, else the only band), so the flow's one Size_property_selected carries a known
  // origin instead of a second false / false one racing the prefilled pick (ISSUE-4022).
  useEffect(() => {
    if (isEmbed) return
    if (availableSegments.length === 1) {
      setSegment(availableSegments[0], 0)
    }

    // Excluded setSegment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSegments, isEmbed])

  useEffect(() => {
    if (getProductCatalogue.isIdle) resetProducts()
  }, [getProductCatalogue.isIdle])

  return {
    ...computedProductProperties,
    ...productInventory,
    hasFloorPlanProduct,
    requiresEnergyCertificateConfig,
    requiresConsumptionCertificateConfig,
    hasDocumentProduct,
    hasMeasurementProduct,
    isOnlyMeasurementProduct,
    hasConsumptionCertificateProduct,
    hasEnergyCertificateProduct,
    hasFloorPlanCertificationProduct,
    requiresAIEditingConfig,
    floorPlanProduct,
    isSkyReplacementEnabled,
    selectedSkyReplacement,
    setSelectedSkyReplacement,
    setIsSkyReplacementEnabled,
    toggleFilterType,
    toggleFilterKind,
    setProductComment,
    updateSelectedProduct,
    setSegment,
    selectProduct,
    unselectProduct,
    selectOptionProduct,
    unselectOptionProduct,
    updateProductPrices,
    selectedProductsKey,
    selectedProductOptionsKey,
  }
})
